import { ViewportPortal } from '@xyflow/react';
import type { CSSProperties } from 'react';
import type { CanvasAwarenessState } from './awareness-types';
import styles from './remote-selection-outlines.module.css';

interface Bounds { x: number; y: number; width: number; height: number; }

export interface RemoteSelectionOutlinesProps {
  remoteStates: Map<number, CanvasAwarenessState>;
  /**
   * Returns the FLOW-SPACE bounds of a node — i.e. the position the node was
   * placed at in the canvas (pre-pan, pre-zoom) plus its measured size. The
   * outline renderer relies on ViewportPortal to apply the viewport transform.
   */
  nodeBoundsResolver: (nodeId: string) => Bounds | null;
}

/**
 * Renders colored outlines around remote-selected nodes via ViewportPortal.
 * Bounds + offsets stay in flow-space; React Flow applies the viewport
 * transform automatically. No manual viewport math, no CSS variables for
 * pan/zoom.
 */
export function RemoteSelectionOutlines({
  remoteStates,
  nodeBoundsResolver,
}: RemoteSelectionOutlinesProps) {
  // Group: nodeId -> peers (in iteration order) so we can offset stacked outlines.
  const byNode = new Map<string, Array<{ peerId: number; state: CanvasAwarenessState }>>();
  remoteStates.forEach((state, peerId) => {
    // Defensive: peers running older canvas code (or non-canvas awareness
    // consumers like other editors) may publish a state with `user`
    // but no `canvasSelection` field. Treat missing/non-array as empty.
    const selection = Array.isArray(state.canvasSelection) ? state.canvasSelection : [];
    selection.forEach((nodeId) => {
      const list = byNode.get(nodeId) ?? [];
      list.push({ peerId, state });
      byNode.set(nodeId, list);
    });
  });

  const items: Array<{ key: string; bounds: Bounds; color: string; offset: number }> = [];
  byNode.forEach((peers, nodeId) => {
    const b = nodeBoundsResolver(nodeId);
    if (!b) return;
    peers.forEach(({ peerId, state }, i) => {
      items.push({ key: `${peerId}-${nodeId}`, bounds: b, color: state.user.color, offset: i * 2 });
    });
  });

  if (items.length === 0) return null;

  return (
    <ViewportPortal>
      {items.map(({ key, bounds, color, offset }) => (
        <div
          key={key}
          data-testid="remote-outline"
          className={styles.outline}
          // CSS custom-property passthrough — color + flow-space rect. The
          // .module.css positions absolutely inside the ViewportPortal's
          // transformed parent.
          style={{
            '--outline-color': color,
            '--outline-left-px': `${bounds.x - offset}px`,
            '--outline-top-px': `${bounds.y - offset}px`,
            '--outline-width-px': `${bounds.width + offset * 2}px`,
            '--outline-height-px': `${bounds.height + offset * 2}px`,
          } as CSSProperties}
        />
      ))}
    </ViewportPortal>
  );
}
